import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Container, Row, Col, Button, Spinner } from 'react-bootstrap';
import { Coin, Globe2, Pen, People, Tree, ChevronRight, Shield, Building, Gear } from 'react-bootstrap-icons';
import '../../HomePage.css';
import './MexicoHomePage.css';
import DeskCarousel from '../carousel/DeskCarousel';
import TopicFeedCarousels, { departmentLinkFor, destinationFor } from '../carousel/TopicFeedCarousels';
import { mexicoFeedsConfig, getMexicanStateInfo } from '../../utils/mexicoFeedsConfig';
import { getMobileFeedsForSection } from '../../utils/feedsTransformer';
import { getSectionsInfo } from '../../utils/multiCountryConfig.js';
import openCapacitorSite from '../../utils/browserUtils';
import FeedItem from '../feed/FeedItem';
import FeedSection from '../feed/FeedSection';
import LoadMoreButton from '../feed/LoadMoreButton';
import IntegratedSearchNavBar from '../navigation/IntegratedSearchNavBar';
import NotificationButton from '../notifications/NotificationButton';
import { useEffect, useState, useCallback } from 'react';
import useFeedData from '../../hooks/useFeedData';
import usePagedFeed from '../../hooks/usePagedFeed';
import useRankedFeed from '../../hooks/useRankedFeed';
import HomeFeedSection from '../feed/HomeFeedSection';
import EmptyNotificationsMessage from '../notifications/EmptyNotificationMessage';

// Section-icon visibility (removed from Preferences — icons off everywhere).
const useShowIcons = () => false;

// ═══════════════════════════════════════════════════════════════════
// COUNTRY CONFIG
// ═══════════════════════════════════════════════════════════════════
// Recents come from a single schema-level feed:
//   /posts/recent/schema?limit=<n>&skip=0&schema=mexico
// It returns a recency-ordered LIST in one call, so every recents view renders
// one card PER ITEM in that list (not one card per config feed). Clicks resolve
// to /feed/mexico/{collection table}, never to /feed/public/news_items.

const COUNTRY = 'mexico';
const COUNTRY_SCHEMA = 'mexico';
const RECENTS_PAGE = `/${COUNTRY}/recents`;

// Short country code used by getSectionsInfo and by notification feed IDs —
// the same value SectionPage was mounted with, so existing subscriptions match.
const COUNTRY_CODE = 'mx';
const HOME_PATH = '/mexico';

// Recents paging. The first load is deliberately longer than a page so the list
// is worth scrolling before anyone has to press anything; each press of Load
// more then adds RECENTS_PAGE_SIZE on top. If the API caps its own page size
// below the initial count that's fine — usePagedFeed detects the end of the
// feed by result rather than by comparing counts against the request.
const RECENTS_FULL_COUNT = 40;
const RECENTS_PAGE_SIZE = 20;

// Turn a human-readable collection name into the snake_case table identifier
// the API/routing uses, e.g.
//   "Suprema Corte de Justicia de la Nación" -> "suprema_corte_de_justicia_de_la_nacion"
function slugifyCollection(name) {
  return (name || '')
    .toString()
    .normalize('NFD')                  // split accented chars into base + combining mark
    .replace(/[\u0300-\u036f]/g, '')   // strip the combining marks (á -> a, ó -> o, ñ -> n)
    .toLowerCase()
    .trim()
    .replace(/[’'`.,()]/g, '')         // drop punctuation that never appears in table names
    .replace(/\s+/g, '_')              // spaces -> underscores
    .replace(/[^a-z0-9_]/g, '');       // anything else -> removed
}

// Resolve the table a recents item should link to. Prefer an explicit `source`
// when present; otherwise derive it from `collection_name`.
function recentsTableFor(item, fallbackTable = '') {
  if (item && item.source) return item.source;
  const slug = slugifyCollection(item && item.collection_name);
  return slug || fallbackTable;
}

// The single schema-level recents feed.
const RECENT_FEED = mexicoFeedsConfig.recent[0];

// Shared bits for a single recents item card.
const recentsCardProps = (item) => ({
  feedName: item.collection_name || item.source || 'Recents',
  schema: COUNTRY_SCHEMA,
  table: recentsTableFor(item),
  branch: RECENT_FEED.branch,
  topic: RECENT_FEED.topic,
  buttonName: RECENT_FEED.buttonName,
});

// ═══════════════════════════════════════════════════════════════════
// ICONS
// ═══════════════════════════════════════════════════════════════════

const JudicialIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
    <path d="M160-120v-80h480v80H160Zm226-194L160-540l84-86 228 226-86 86Zm254-254L414-796l86-84 226 226-86 86Zm184 408L302-682l56-56 522 522-56 56Z"/>
  </svg>
);

const ClockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
    <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71z"/>
    <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0"/>
  </svg>
);

const HealthIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
    <path d="m8 2.748-.717-.737C5.6.281 2.514.878 1.4 3.053.918 3.995.78 5.323 1.508 7H.43c-2.128-5.697 4.165-8.83 7.394-5.857q.09.083.176.171a3 3 0 0 1 .176-.17c3.23-2.974 9.522.159 7.394 5.856h-1.078c.728-1.677.59-3.005.108-3.947C13.486.878 10.4.28 8.717 2.01zM2.212 10h1.315C4.593 11.183 6.05 12.458 8 13.795c1.949-1.337 3.407-2.612 4.473-3.795h1.315c-1.265 1.566-3.14 3.25-5.788 5-2.648-1.75-4.523-3.434-5.788-5"/>
    <path d="M10.464 3.314a.5.5 0 0 0-.945.049L7.921 8.956 6.464 5.314a.5.5 0 0 0-.88-.091L3.732 8H.5a.5.5 0 0 0 0 1H4a.5.5 0 0 0 .416-.223l1.473-2.209 1.647 4.118a.5.5 0 0 0 .945-.049l1.598-5.593 1.457 3.642A.5.5 0 0 0 12 9h3.5a.5.5 0 0 0 0-1h-3.162z"/>
  </svg>
);

// ═══════════════════════════════════════════════════════════════════
// SECTION MAPS
// ═══════════════════════════════════════════════════════════════════

const SECTION_MAP = {
  recent:        { title: 'Recents',        icon: <ClockIcon />,   branchKey: 'recent',        viewAllLink: RECENTS_PAGE },
  legislative:   { title: 'Legislative',    icon: <Pen />,         branchKey: 'legislative',   viewAllLink: '/mexico/legislative' },
  executive:     { title: 'Executive',      icon: <People />,      branchKey: 'executive',     viewAllLink: '/mexico/executive' },
  judicial:      { title: 'Judicial',       icon: <JudicialIcon />,branchKey: 'judicial',      viewAllLink: '/mexico/judicial' },
  defense:       { title: 'Defense',        icon: <Shield />,      branchKey: 'defense',       viewAllLink: '/mexico/defense' },
  economy:       { title: 'Economy',        icon: <Coin />,        branchKey: 'economy',       viewAllLink: '/mexico/economy' },
  environment:   { title: 'Environment',    icon: <Tree />,        branchKey: 'environment',   viewAllLink: '/mexico/environment' },
  foreignAffairs:{ title: 'Foreign Affairs',icon: <Globe2 />,      branchKey: 'foreignAffairs',viewAllLink: '/mexico/foreignaffairs' },
  health:        { title: 'Health',         icon: <HealthIcon />,  branchKey: 'health',        viewAllLink: '/mexico/health' },
  infrastructure:{ title: 'Infrastructure', icon: <Building />,    branchKey: 'infrastructure',viewAllLink: '/mexico/infrastructure' },
};

const DESKTOP_SECTION_MAP = {
  legislative:   { title: 'Legislative',    icon: <Pen />,         sections: mexicoFeedsConfig.legislative,   viewAllLink: '/mexico/legislative' },
  executive:     { title: 'Executive',      icon: <People />,      sections: mexicoFeedsConfig.executive,     viewAllLink: '/mexico/executive' },
  judicial:      { title: 'Judicial',       icon: <JudicialIcon />,sections: mexicoFeedsConfig.judicial,      viewAllLink: '/mexico/judicial' },
  defense:       { title: 'Defense',        icon: <Shield />,      sections: mexicoFeedsConfig.defense,       viewAllLink: '/mexico/defense' },
  economy:       { title: 'Economy',        icon: <Coin />,        sections: mexicoFeedsConfig.economy,       viewAllLink: '/mexico/economy' },
  environment:   { title: 'Environment',    icon: <Tree />,        sections: mexicoFeedsConfig.environment,   viewAllLink: '/mexico/environment' },
  foreignAffairs:{ title: 'Foreign Affairs',icon: <Globe2 />,      sections: mexicoFeedsConfig.foreignAffairs,viewAllLink: '/mexico/foreignaffairs' },
  health:        { title: 'Health',         icon: <HealthIcon />,  sections: mexicoFeedsConfig.health,        viewAllLink: '/mexico/health' },
  infrastructure:{ title: 'Infrastructure', icon: <Building />,    sections: mexicoFeedsConfig.infrastructure,viewAllLink: '/mexico/infrastructure' },
};

const ALL_SECTIONS_ORDER = [
  'recent', 'legislative', 'executive', 'judicial',
  'defense', 'economy', 'environment', 'foreignAffairs',
  'health', 'infrastructure',
];

// ═══════════════════════════════════════════════════════════════════
// URL <-> TOPIC
// ═══════════════════════════════════════════════════════════════════
// Topic URLs (/mexico/legislative, ...) route to THIS page in App.js — see
// MX_SECTION_PATHS there, which must stay in sync with the viewAllLink values
// below. The path is the source of truth for those topics: pasting
// /mexico/economy opens the home page with Economy already selected, and
// picking a topic in the filter bar pushes the matching path without leaving
// the page.
//
// Only topics present in BOTH maps are routed here, so nothing can land on an
// empty view. /mexico/immigration and /mexico/justice have routes but no entry
// in either map, so they stay on SectionPage until they get one.
const ROUTE_TO_TOPIC = Object.entries(SECTION_MAP).reduce((acc, [key, sec]) => {
  if (key === 'recent' || !DESKTOP_SECTION_MAP[key]) return acc;
  acc[sec.viewAllLink] = key;
  return acc;
}, {});

const SECTION_TOPICS = new Set(Object.values(ROUTE_TO_TOPIC));

// Trailing slashes and percent-encoding shouldn't decide which topic we show.
const normalizePath = (pathname) => {
  let path = pathname || '';
  try { path = decodeURIComponent(path); } catch (e) { /* leave as-is */ }
  return path.length > 1 ? path.replace(/\/+$/, '') : path;
};

const topicFromPath = (pathname) => ROUTE_TO_TOPIC[normalizePath(pathname)] || null;

// Same mapping SectionPage used, so notification topics keep matching the
// subscriptions people already have.
const getSectionBranch = (sectionKey) => {
  switch (sectionKey) {
    case 'defense':
    case 'defence':
      return 'defense';
    case 'foreign-affairs':
    case 'foreignaffairs':
    case 'foreignAffairs':
      return 'foreign affairs';
    case 'science-tech':
    case 'sciencetech':
    case 'science':
      return 'science & tech';
    default:
      return sectionKey;
  }
};

// Topic header: title, Learn more link, notification bell. Rendered only for
// topics that own a URL, so Glance / Recents / Following / All stay bare.
const SectionTopicHeader = ({ topicKey, isMobile }) => {
  const sec = SECTION_MAP[topicKey];
  if (!sec) return null;

  const info = (getSectionsInfo(COUNTRY_CODE) || {})[sec.branchKey] || {};
  const learnMoreLink = info.learnMoreLink || '';
  const branch = getSectionBranch(sec.branchKey);

  return (
    <div className="section-topic-header">
      <h3 className="section-topic-title">{sec.title}</h3>
      <div className="section-topic-actions">
        {/* {learnMoreLink && (
          <Link
            to="#"
            className="section-topic-learn-more"
            onClick={(e) => { e.preventDefault(); openCapacitorSite(learnMoreLink, '_blank'); }}
          >
            Learn more
          </Link>
        )} */}
        <NotificationButton
          feedId={`${sec.branchKey}-branch-${COUNTRY_CODE}`}
          feedName={`${sec.title} Updates`}
          branch={branch}
          topic={branch}
          country={COUNTRY_CODE}
          variant="outline-light"
          size="sm"
          showLabel={!isMobile}
        />
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════
// FOLLOWING FEED HELPERS
// ═══════════════════════════════════════════════════════════════════

const TOPIC_TO_SECTION = {
  legislative: 'legislative', executive: 'executive', judicial: 'judicial',
  defense: 'defense', economy: 'economy', elections: 'elections',
  environment: 'environment', foreignaffairs: 'foreignAffairs', health: 'health',
  science_and_tech: 'science', spending: 'spending', infrastructure: 'infrastructure',
  regulations: 'regulations', justice: 'justice',
};

const TOPIC_PREF_KEYS = {
  legislative: 'MexicoLegislativeNews', executive: 'MexicoExecutiveNews', judicial: 'MexicoJudicialNews',
  defense: 'MexicoDefenseNews', economy: 'MexicoEconomyNews', elections: 'MexicoElectionNews',
  environment: 'MexicoEnvironmentNews', foreignaffairs: 'MexicoForeignAffairsNews', health: 'MexicoHealthNews',
  science_and_tech: 'MexicoScienceTechNews', spending: 'MexicoSpendingNews',
  infrastructure: 'MexicoInfrastructureNews', regulations: 'MexicoRegulationsNews', justice: 'MexicoJusticeNews',
};

function getFollowedFeeds() {
  const followedTopics = Object.entries(TOPIC_PREF_KEYS)
    .filter(([_, prefKey]) => localStorage.getItem(prefKey) === 'true')
    .map(([topic]) => topic);
  const result = [];
  followedTopics.forEach((topicKey) => {
    const sectionKey = TOPIC_TO_SECTION[topicKey];
    if (!sectionKey) return;
    const entries = mexicoFeedsConfig[sectionKey];
    if (!Array.isArray(entries)) return;
    entries.forEach((feed) => {
      if (feed.type === 'normal' && feed.apiEndpoint) {
        result.push(feed);
      } else if (feed.type === 'carousel' && feed.carouselItems) {
        feed.carouselItems.forEach((item) => {
          if (item.type === 'viewall' || item.type === 'member' || !item.apiEndpoint) return;
          result.push({ ...item, feedName: item.feedName || item.title, buttonName: item.buttonName || 'Document' });
        });
      }
    });
  });
  return { feeds: result, hasFollowedTopics: followedTopics.length > 0 };
}

// ═══════════════════════════════════════════════════════════════════
// RECENTS RENDERING (one card per item from the single schema feed)
// ═══════════════════════════════════════════════════════════════════

// Mobile vertical list of recents items (used in the dedicated recents view).
// The list grows in place behind a Load more button; paging and end-of-feed
// detection live in usePagedFeed, shared with the other country home pages.
const MobileRecentsList = ({ count = RECENTS_FULL_COUNT }) => {
  const { items, loading, isFirstLoad, hasMore, loadMore } = usePagedFeed(
    RECENT_FEED.apiEndpoint,
    { initialCount: count, pageSize: RECENTS_PAGE_SIZE }
  );
  if (isFirstLoad) return (
    <div className="d-flex justify-content-center py-4"><Spinner animation="grow" variant="light" /></div>
  );
  if (items.length === 0) return null;
  return (
    <div>
      {items.map((item, idx) => {
        const p = recentsCardProps(item);
        return (
          <div key={item.id || idx} className="mb-4">
            <FeedItem item={item} feedName={p.feedName} branch={p.branch} topic={p.topic} buttonName={p.buttonName} schema={p.schema} table={p.table} expanded={false} isRecentsItem={true} />
          </div>
        );
      })}
      {hasMore && <LoadMoreButton onLoadMore={loadMore} loading={loading} />}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// CAROUSEL COMPONENTS (non-recents branches)
// ═══════════════════════════════════════════════════════════════════

const MobileCarouselCard = ({ feed }) => {
  const { data, loading, error } = useFeedData(feed.apiParams, 1);
  if (loading) return <div className="mobile-carousel-loading"><Spinner animation="grow" variant="light" /></div>;
  if (error || !data || data.length === 0) return <div className="mobile-carousel-empty"><p>{error ? 'Error loading content' : 'No content available'}</p></div>;
  const urlParams = new URLSearchParams(feed.apiParams);
  const schema = urlParams.get('schema') || 'mexico';
  const table = urlParams.get('table');
  return (
    <div style={{ height: '160px', minHeight: '160px', overflow: 'hidden' }}>
      <FeedItem item={data[0]} feedName={feed.name} branch={feed.feedBranch} topic={feed.feedTopic} buttonName={feed.buttonName} schema={schema} table={table} expanded={false} variant="homepage" />
    </div>
  );
};

const HomePageBranchCarousel = ({ title, icon, branchKey, viewAllLink, isFirstCarousel = false }) => {
  const navigate = useNavigate();
  const showIcons = useShowIcons();
  const allBranchFeeds = getMobileFeedsForSection(mexicoFeedsConfig, branchKey).filter(f => f.showOnHomePage !== false);
  // A feed inside a department (a secretaría) goes to that department's page;
  // everything else to its own full-feed view.
  const handleFeedClick = (feed) => {
    const target = departmentLinkFor(feed) || destinationFor(feed, COUNTRY_SCHEMA);
    if (!target) return;
    window.scrollTo(0, 0);
    navigate(target);
  };
  return (
    <div className={`homepage-branch-carousel ${isFirstCarousel ? 'homepage-first-carousel' : ''}`}>
      <div className="branch-header">
        <div className="branch-title" onClick={() => navigate(viewAllLink)}>{showIcons && icon}<span>{title}</span><ChevronRight className="branch-chevron" /></div>
      </div>
      <div className="carouselGroupContainer news-section">
        <div className="scroll-container">
          <div className="gradient-left" /><div className="gradient-right" />
          <div className="scroll-area"><div className="card-container">
            {allBranchFeeds.map((feed) => (
              <div key={feed.id} className="card-wrapper">
                {feed.name !== title && (
                  <div className="mobile-feed-heading-row">
                    <h5 className="feed-heading-mobile" onClick={() => handleFeedClick(feed)}>
                      <div className="title-container"><span className="heading-text2">{feed.name}</span><ChevronRight className="heading-chevron" /></div>
                    </h5>
                  </div>
                )}
                <div className="card-content"><div className="courtNewsMobile"><MobileCarouselCard feed={feed} /></div></div>
              </div>
            ))}
            <div className="card-wrapper view-all-card">
              <div id="viewFullPageButton"><Button id="viewFullLegislativePageButtonMobile" size="lg" onClick={() => navigate(viewAllLink)}>View all {title} news</Button></div>
            </div>
          </div></div>
        </div>
      </div>
    </div>
  );
};

// The topic view is the old section page: EVERY feed in the branch, not just the
// ones flagged for the home overview. showOnHomePage decides what appears in
// Glance and All Categories; filtering on it here hid most of each branch behind
// a couple of feeds. Grouping, department links and committee feeds all live in
// TopicFeedCarousels, shared with the other country home pages.
const TopicExpandedView = ({ topicKey }) => {
  const sec = SECTION_MAP[topicKey];
  if (!sec) return null;
  const feeds = getMobileFeedsForSection(mexicoFeedsConfig, sec.branchKey);
  return <TopicFeedCarousels feeds={feeds} itemCount={5} fallbackSchema={COUNTRY_SCHEMA} />;
};

// ═══════════════════════════════════════════════════════════════════
// MOBILE CONTENT
// ═══════════════════════════════════════════════════════════════════

const MobileFeedList = ({ apiEndpoint, feedName, branch, topic, buttonName, maxItems = 10 }) => {
  const { data, loading } = useFeedData(apiEndpoint, maxItems);
  const urlParams = new URLSearchParams((apiEndpoint || '').replace(/^[&?]/, ''));
  const schema = urlParams.get('schema') || '';
  const table = urlParams.get('table') || '';
  if (loading && (!data || data.length === 0)) return (
    <div className="d-flex justify-content-center py-4"><Spinner animation="grow" variant="light" /></div>
  );
  if (!data || data.length === 0) return null;
  return (
    <div>
      {data.map((item, idx) => (
        <div key={item.id || idx} className="mb-4">
          <FeedItem item={item} feedName={feedName} branch={branch} topic={topic} buttonName={buttonName || 'Document'} schema={schema} table={table} expanded={false} />
        </div>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// GLANCE (AI-ranked feed)
// ═══════════════════════════════════════════════════════════════════

// Glance full list (mobile): the default home view. Vertical list of ranked
// items, no heading. Falls back to the Recents list if the ranked feed can't
// load.
const MobileGlance = () => {
  const { items, loading, error } = useRankedFeed(COUNTRY);
  return (
    <>
      {loading && (<div className="d-flex justify-content-center py-4"><Spinner animation="grow" variant="light" /></div>)}
      {!loading && error && (<div style={{ padding: '0 10px' }}><MobileRecentsList count={RECENTS_FULL_COUNT} /></div>)}
      {!loading && !error && (
        <div style={{ padding: '0 10px' }}>
          {items.map((item, idx) => (
            <div key={item.id || idx} className="mb-4">
              <FeedItem item={item} feedName={item.collection_name || item.source || 'Glance'} branch={item.branch || 'N/A'} topic={item.topic || 'N/A'} buttonName={item.buttonName || 'Document'} schema={COUNTRY_SCHEMA} table={item.source || ''} expanded={false} />
            </div>
          ))}
        </div>
      )}
    </>
  );
};

// All view (mobile): topic carousels only. No Glance, Recents, or report.
const MobileAllView = () => (
  <>
    {ALL_SECTIONS_ORDER.filter((key) => key !== 'recent').map((key) => {
      const sec = SECTION_MAP[key];
      if (!sec) return null;
      return <HomePageBranchCarousel key={key} title={sec.title} icon={sec.icon} branchKey={sec.branchKey} viewAllLink={sec.viewAllLink} />;
    })}
  </>
);

const MobileContent = ({ activeTopic, activeFeed }) => {
  const navigate = useNavigate();

  // ── Single feed selected → vertical list for that one feed ──
  if (activeFeed && activeFeed.id && activeFeed.apiEndpoint) {
    return (
      <div style={{ padding: '0 10px' }}>
        <div
          style={{ padding: '8px 10px 8px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}
          onClick={() => {
            const target = destinationFor(activeFeed, COUNTRY_SCHEMA);
            if (target) navigate(target);
          }}
        >
          <span style={{ fontSize: '17px', fontWeight: 'bold', color: 'var(--text-primary)' }}>{activeFeed.title || activeFeed.feedName}</span>
          <ChevronRight style={{ width: '16px', height: '16px', color: 'var(--text-primary)' }} />
        </div>
        <MobileFeedList apiEndpoint={activeFeed.apiEndpoint} feedName={activeFeed.feedName || activeFeed.title} branch={activeFeed.branch} topic={activeFeed.topic} buttonName={activeFeed.buttonName} maxItems={20} />
      </div>
    );
  }

  // ── Following feed ──
  if (activeTopic === 'following') {
    const { feeds: followedFeeds, hasFollowedTopics } = getFollowedFeeds();
    const settingsBtn = (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '8px 0 12px' }}>
        {/* <Link to="/notifications/settings" style={{ marginLeft: 'auto', display: 'inline-flex' }}>
          <Button variant="link" style={{ color: 'var(--text-primary)', padding: '4px', border: 'none', background: 'none', boxShadow: 'none' }}>
            <Gear size={18} />
          </Button>
        </Link> */}
      </div>
    );
    if (!hasFollowedTopics || followedFeeds.length === 0) {
      return (
        <div style={{ padding: '0 10px' }}>
          {settingsBtn}
          <EmptyNotificationsMessage />
        </div>
      );
    }
    return (
      <div style={{ padding: '0 10px' }}>
        {settingsBtn}
        <HomeFeedSection feeds={followedFeeds} itemsPerFeed={10} searchQuery="" />
      </div>
    );
  }

  if (activeTopic === 'glance') {
    return <MobileGlance />;
  }

  if (activeTopic === 'all') {
    return <MobileAllView />;
  }

  if (activeTopic === 'recent') {
    return (
      <div style={{ padding: '0 10px' }}>
        <MobileRecentsList count={RECENTS_FULL_COUNT} />
      </div>
    );
  }

  return <TopicExpandedView topicKey={activeTopic} />;
};

// ═══════════════════════════════════════════════════════════════════
// DESKTOP HELPERS
// ═══════════════════════════════════════════════════════════════════

function flattenSectionFeeds(categoryFeeds) {
  if (!Array.isArray(categoryFeeds)) return [];
  const result = [];
  categoryFeeds.forEach((feed) => {
    if (feed.type === 'normal') {
      result.push(feed);
    } else if (feed.type === 'carousel' && feed.carouselItems) {
      feed.carouselItems.forEach((item) => {
        if (item.type === 'viewall' || item.type === 'member') return;
        result.push({
          ...item,
          branch: item.branch || feed.branch,
          topic: item.topic || feed.topic,
          viewAllLink: item.viewAllLink || feed.departmentLink,
        });
      });
    }
  });
  return result;
}

// ═══════════════════════════════════════════════════════════════════
// DESKTOP CONTENT
// ═══════════════════════════════════════════════════════════════════

// Desktop dedicated recents view: vertical list of item cards from the one feed,
// growing in place behind the same Load more button as mobile.
const DesktopRecentsFullList = ({ count = RECENTS_FULL_COUNT }) => {
  const { items, loading, isFirstLoad, hasMore, loadMore } = usePagedFeed(
    RECENT_FEED.apiEndpoint,
    { initialCount: count, pageSize: RECENTS_PAGE_SIZE }
  );
  if (isFirstLoad) return (
    <div className="d-flex justify-content-center py-4"><Spinner animation="grow" variant="light" /></div>
  );
  if (items.length === 0) return null;
  return (
    <>
      <div className="feed-items">
        {items.map((item, idx) => {
          const p = recentsCardProps(item);
          return (
            <FeedItem
              key={item.id || idx}
              item={item}
              feedName={p.feedName}
              branch={p.branch}
              topic={p.topic}
              buttonName={p.buttonName}
              schema={p.schema}
              table={p.table}
              variant="homepage"
              showViewMore={true}
              viewMoreUrl={p.table ? `/feed/${p.schema}/${p.table}` : null}
              isRecentsItem={true}
            />
          );
        })}
      </div>
      {hasMore && <LoadMoreButton onLoadMore={loadMore} loading={loading} />}
    </>
  );
};

// Glance full list (desktop): the default home view. Stacked ranked items, no
// heading. Falls back to the Recents list if the ranked feed can't load.
const DesktopGlance = () => {
  const { items, loading, error } = useRankedFeed(COUNTRY);
  return (
    <Container fluid className="desktop-list-wrap">
      <div id="recentsCon" className="desktop-single-feed">
        <Col id="homeCol">
          <div id="columnLefttPadding">
            {loading && (<div className="d-flex justify-content-center py-5"><Spinner animation="grow" variant="light" /></div>)}
            {!loading && error && <DesktopRecentsFullList count={RECENTS_FULL_COUNT} />}
            {!loading && !error && items.map((item, idx) => (
              <FeedItem key={item.id || idx} item={item} feedName={item.collection_name || item.source || 'Glance'} branch={item.branch || 'N/A'} topic={item.topic || 'N/A'} buttonName={item.buttonName || 'Document'} schema={COUNTRY_SCHEMA} table={item.source || ''} expanded={false} />
            ))}
          </div>
        </Col>
      </div>
    </Container>
  );
};

// All view (desktop): topic-carousel grid only. No Glance, Recents, or report.
const DesktopAllView = () => {
  const showIcons = useShowIcons();
  const carouselKeys = ALL_SECTIONS_ORDER.filter(k => k !== 'recent');
  const rows = [];
  for (let i = 0; i < carouselKeys.length; i += 3) rows.push(carouselKeys.slice(i, i + 3));
  return (
    <Container fluid>
      {rows.map((row, ri) => (
        <Row id="branchesRow" key={ri}>
          {row.map((key) => {
            const sec = DESKTOP_SECTION_MAP[key];
            if (!sec || !sec.sections) return null;
            return <Col md={4} key={key}><DeskCarousel title={sec.title} icon={showIcons ? sec.icon : null} sections={sec.sections} viewAllLink={sec.viewAllLink} intervalDuration={30000} /></Col>;
          })}
        </Row>
      ))}
    </Container>
  );
};

const DesktopContent = ({ activeTopic, activeFeed }) => {
  const navigate = useNavigate();
  const showIcons = useShowIcons();

  // ── Glance (AI-ranked) → default home view ──
  if (activeTopic === 'glance') {
    return <DesktopGlance />;
  }

  // ── Following feed ──
  if (activeTopic === 'following') {
    const { feeds: followedFeeds, hasFollowedTopics } = getFollowedFeeds();
    const desktopHeader = (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: '14px' }}>
        {/* <Link to="/notifications/settings" style={{ marginLeft: 'auto', display: 'inline-flex' }}>
          <Button variant="link" style={{ color: 'var(--text-primary)', padding: '4px', border: 'none', background: 'none', boxShadow: 'none' }}>
            <Gear size={18} />
          </Button>
        </Link> */}
      </div>
    );
    if (!hasFollowedTopics) {
      return (
        <Container fluid className="desktop-list-wrap">
          <Row id="branchesRow">
            <Col md={12}>
              <div id="recentsCon" className="desktop-single-feed">
                <Col id="homeCol">
                  <div id="columnLefttPadding">
                    {desktopHeader}
                    <EmptyNotificationsMessage />
                  </div>
                </Col>
              </div>
            </Col>
          </Row>
        </Container>
      );
    }
    return (
      <Container fluid className="desktop-list-wrap">
        <Row id="branchesRow">
          <Col md={12}>
            <div id="recentsCon" className="desktop-single-feed">
              <Col id="homeCol">
                <div id="columnLefttPadding">
                  {desktopHeader}
                  <HomeFeedSection feeds={followedFeeds} itemsPerFeed={10} searchQuery="" />
                </div>
              </Col>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  // ── Single feed selected → full-width with clickable heading ──
  if (activeFeed && activeFeed.id && activeFeed.apiEndpoint) {
    const feedUrl = destinationFor(activeFeed, COUNTRY_SCHEMA) || activeFeed.viewAllLink || null;
    return (
      <Container fluid>
        <Row id="branchesRow" style={{ marginBottom: '24px' }}>
          <Col md={12}>
            <div id="recentsCon" className="desktop-single-feed">
              <Col id="homeCol">
                <div id="columnLefttPadding">
                  {(activeFeed.title || activeFeed.feedName) && (
                    <h4
                      onClick={() => { if (feedUrl) { window.scrollTo(0, 0); navigate(feedUrl); } }}
                      style={{
                        fontWeight: 700, fontSize: '18px', color: 'var(--text-primary)',
                        marginBottom: '14px', paddingBottom: '8px',
                        borderBottom: '1px solid var(--border-color, rgba(128,128,128,0.2))',
                        cursor: feedUrl ? 'pointer' : 'default',
                        display: 'flex', alignItems: 'center', gap: '6px',
                      }}
                    >
                      {activeFeed.title || activeFeed.feedName}
                      <ChevronRight style={{ width: '18px', height: '18px' }} />
                    </h4>
                  )}
                  <FeedSection
                    apiEndpoint={activeFeed.apiEndpoint}
                    feedName={activeFeed.feedName || activeFeed.title}
                    branch={activeFeed.branch}
                    topic={activeFeed.topic}
                    buttonName={activeFeed.buttonName}
                    viewAllLink={activeFeed.viewAllLink}
                    maxItems={10}
                    showViewMore={true}
                  />
                </div>
              </Col>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  if (activeTopic !== 'all') {
    // ── Recents → vertical list of items ──
    if (activeTopic === 'recent') {
      return (
        <Container fluid className="desktop-list-wrap">
          <div id="recentsCon" className="desktop-single-feed">
            <Col id="homeCol">
              <div id="columnLefttPadding">
                <DesktopRecentsFullList count={RECENTS_FULL_COUNT} />
              </div>
            </Col>
          </div>
        </Container>
      );
    }

    // ── Specific topic → 3-col grid of DeskCarousels ──
    const sec = DESKTOP_SECTION_MAP[activeTopic];
    if (!sec || !sec.sections) return null;
    const feeds = sec.sections;
    const rows = [];
    for (let i = 0; i < feeds.length; i += 3) rows.push(feeds.slice(i, i + 3));
    return (
      <Container fluid>
        {rows.map((row, ri) => (
          <Row id="branchesRow" key={ri} style={{ marginBottom: '24px' }}>
            {row.map((feed, fi) => {
              const feedTitle = feed.type === 'carousel'
                ? (feed.departmentTitle || feed.title)
                : (feed.title || feed.feedName);
              const feedViewAllLink = feed.type === 'carousel'
                ? (feed.departmentLink || sec.viewAllLink)
                : (feed.viewAllLink || sec.viewAllLink);
              return (
                <Col md={4} key={feed.id || fi}>
                  <DeskCarousel
                    title={feedTitle}
                    icon={showIcons ? sec.icon : null}
                    sections={[feed]}
                    viewAllLink={feedViewAllLink}
                    intervalDuration={30000}
                    showAll={true}
                    showFeedTitle={false}
                  />
                </Col>
              );
            })}
          </Row>
        ))}
      </Container>
    );
  }

  // ── All topics → topic-carousel grid only ──
  return <DesktopAllView />;
};

// ═══════════════════════════════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════════════════════════════

const MexicoHomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  // The path decides the topic on first paint, so /mexico/economy renders the
  // Economy view straight away — no flash of Glance, no redirect.
  const [activeTopic, setActiveTopic] = useState(() => topicFromPath(location.pathname) || 'glance');
  const [activeFeed, setActiveFeed] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const check = () => {
      const ua = navigator.userAgent.toLowerCase();
      setIsMobile(/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(ua) || window.innerWidth <= 768);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Keep the view in step with the URL — covers back/forward, in-app links and
  // a pasted address. Selecting a feed doesn't change the path, so this doesn't
  // fire and the feed selection survives.
  useEffect(() => {
    const urlTopic = topicFromPath(location.pathname);
    if (urlTopic) {
      setActiveTopic((prev) => (prev === urlTopic ? prev : urlTopic));
      setActiveFeed(null);
      return;
    }
    // Back on /mexico: only reset when we came from a topic URL. Glance,
    // Recents, Following and All set their own topic and must not be clobbered.
    setActiveTopic((prev) => (SECTION_TOPICS.has(prev) ? 'glance' : prev));
  }, [location.pathname]);

  const handleSearch = useCallback((query) => {
    const trimmed = typeof query === 'string' ? query.trim() : (query?.target?.value || '').trim();
    setSearchQuery(trimmed);
    if (trimmed) navigate(`/search/mexico?query=${encodeURIComponent(trimmed)}`);
  }, [navigate]);

  const handleFilterChange = useCallback(({ category, feed, stateKey }) => {
    if (stateKey) {
      const stateInfo = getMexicanStateInfo(stateKey);
      const path = stateInfo
        ? stateInfo.fullName.toLowerCase()
            .replace(/\s+/g, '')
            .replace(/á/g, 'a').replace(/é/g, 'e').replace(/í/g, 'i')
            .replace(/ó/g, 'o').replace(/ú/g, 'u').replace(/ñ/g, 'n')
        : stateKey;
      navigate(`/mexico/${path}`);
      return;
    }

    const nextTopic = category || 'all';
    // Treat the synthetic 'recent_all' placeholder as no specific feed selected
    const hasFeed = !!(feed && feed.id && feed.id !== 'recent_all' && feed.apiEndpoint);

    setActiveTopic(nextTopic);
    setActiveFeed(hasFeed ? feed : null);

    // Put the topic in the address bar. Topics with a page of their own get it;
    // everything else goes back to /mexico. Same page either way — App.js routes
    // both here.
    const target = SECTION_TOPICS.has(nextTopic) ? SECTION_MAP[nextTopic].viewAllLink : HOME_PATH;
    if (normalizePath(location.pathname) !== target) navigate(target);

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [navigate, location.pathname]);

  const feedFilterProps = {
    onFilterChange: handleFilterChange,
    currentSchema: 'mexico',
    currentTable: '',
    currentFilterColumn: '',
    currentFilterString: '',
    currentCommittee: '',
    data: [],
    currentOrderBy: 'created_at',
    allowedOrderBy: [],
    onSortChange: () => {},
    currentCategory: activeTopic === 'all' ? null : activeTopic,
    hideSortButton: true,
    hideLevel: true,
    // This page renders its own view for every topic — Glance, All Categories,
    // Recents, Following, and a per-topic grid — so a Topic change must NOT
    // resolve a feed. With auto-select on, picking All Categories flattened
    // every category and handed back the first loadable feed, which landed in
    // the single-feed branch of Desktop/MobileContent and hid the All view
    // entirely; picking a topic did the same to the topic grid. Only an explicit
    // pick in the Feed dropdown sets activeFeed now.
    autoSelectFeed: false,
  };

  return (
    <>
      <IntegratedSearchNavBar
        searchQuery={searchQuery}
        onSearchChange={handleSearch}
        currentCountry="mexico"
        placeholder="Search Gov Glance"
        feedFilterProps={feedFilterProps}
      />
      <div id="homebg" className="mexico-homepage-root" style={{ paddingTop: 0 }}>
        {SECTION_TOPICS.has(activeTopic) && (
          <SectionTopicHeader topicKey={activeTopic} isMobile={isMobile} />
        )}
        {isMobile
          ? <MobileContent activeTopic={activeTopic} activeFeed={activeFeed} />
          : <DesktopContent activeTopic={activeTopic} activeFeed={activeFeed} />
        }
      </div>
      <style>{`
        .desktop-single-feed .feed-items > * + * { margin-top: 24px; }

        /* ─── Topic header (title + Learn more + notification bell) ─── */
        .section-topic-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 8px 20px 4px;
        }
        .section-topic-title {
          margin: 0;
          font-size: 17px;
          font-weight: 700;
          color: var(--text-primary);
        }
        .section-topic-actions { display: flex; align-items: center; gap: 12px; }
        .section-topic-learn-more {
          color: var(--accent-hover);
          font-size: 13px;
          white-space: nowrap;
          text-decoration: none;
        }
        .section-topic-learn-more:hover { text-decoration: underline; }

        @media screen and (min-width: 768px) {
          .section-topic-header { padding: 16px 24px 8px; }
          .section-topic-title { font-size: 22px; }
        }

        /* ─── Desktop single-column views (Glance, Recents, Following): narrow, centered ─── */
        .desktop-list-wrap {
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
        }
      `}</style>
    </>
  );
};

export default MexicoHomePage;